import { clampWindowStart, enumerateDates } from '~utils/enumerate-dates';
import type {
    KpiCardData,
    KpiCardDataRequest,
    KpiCardState,
    KpiForm,
    KpiSource,
} from '../types';

/**
 * Deterministic sample data per form, so a card renders its real layout (grayed
 * out) before a real source exists. Scalar forms (number/rate/line/split) read as
 * zeros; chart forms (pie/bars/columns) carry representative non-zero values so
 * their shapes are visible. Adding a new form is a single entry here and nothing
 * else: the exhaustive `Record<KpiForm, ...>` makes the compiler enforce that.
 */
const SAMPLE_DATA: Record<KpiForm, (req: KpiCardDataRequest) => KpiCardData> = {
    number: () => ({ form: 'number', value: 0 }),
    rate: () => ({ form: 'rate', numerator: 0, denominator: 0, rate: null }),
    line: (req) => ({
        form: 'line',
        points: enumerateDates(
            req.period.start,
            req.period.end
        ).map((date) => ({ date, value: 0 })),
    }),
    // The sole placeholder pie is the RPP dashboard's "Games played by
    // environment" card, so the sample uses that design's environment labels
    // (iOS/Android/Web), same as the `split` sample below carries its real
    // "RPP"/"Full app" labels.
    pie: () => ({
        form: 'pie',
        slices: [
            { label: 'iOS', value: 68 },
            { label: 'Android', value: 12 },
            { label: 'Web', value: 10 },
        ],
    }),
    bars: () => ({
        form: 'bars',
        steps: [
            { label: 'Step 1', value: 1000 },
            { label: 'Step 2', value: 710 },
            { label: 'Step 3', value: 520 },
            { label: 'Other', value: 300 },
        ],
    }),
    columns: () => ({
        form: 'columns',
        columns: [
            { label: 'Group A', value: 6200 },
            { label: 'Group B', value: 7600 },
        ],
    }),
    split: () => ({
        form: 'split',
        left: { label: 'RPP', value: 0 },
        right: { label: 'Full app', value: 0 },
    }),
    trio: () => ({ form: 'trio', toDate: 0, weekly: 0, daily: 0 }),
    'rate-trio': () => ({
        form: 'rate-trio',
        toDate: { numerator: 0, denominator: 0, rate: null },
        weekly: { numerator: 0, denominator: 0, rate: null },
        daily: { numerator: 0, denominator: 0, rate: null },
    }),
    downloads: (req) => {
        // Shortened to the longest window the chart can draw, exactly as the
        // real source does: `range` is the axis a renderer must draw WHOLE, so
        // declaring the raw period over points capped at the enumeration bound
        // hands the renderer a range it refuses, and the sample card draws
        // nothing at all. The real card also says the window was shortened;
        // sample data has no reason to carry, hence just the clamp here.
        const start = clampWindowStart(req.period.start, req.period.end);
        const dates = enumerateDates(start, req.period.end);
        // Android's store report lags, so the sample truncates that series: the
        // form's defining shape (a line that ends before the axis does) is
        // visible without a real source behind it.
        const androidDates = dates.slice(0, Math.max(dates.length - 3, 0));
        return {
            form: 'downloads',
            range: { start, end: req.period.end },
            series: [
                {
                    label: 'iOS',
                    total: dates.length * 40,
                    points: dates.map((date) => ({ date, value: 40 })),
                },
                {
                    label: 'Android',
                    total: androidDates.length * 25,
                    points: androidDates.map((date) => ({ date, value: 25 })),
                },
            ],
        };
    },
    multiline: (req) => {
        const start = clampWindowStart(req.period.start, req.period.end);
        const dates = enumerateDates(start, req.period.end);
        // One region lags, same shape as the downloads sample's Android line:
        // the form's defining trait (an axis wider than one of its lines)
        // stays visible without a real source behind it.
        const shortDates = dates.slice(0, Math.max(dates.length - 3, 0));
        const region = (label: string, value: number, points: string[]) => ({
            label,
            value: points.length > 0 ? value : null,
            points: points.map((date) => ({ date, value })),
        });
        return {
            form: 'multiline',
            range: { start, end: req.period.end },
            unit: 'percent',
            series: [
                region('Quebec', 62, dates),
                region('Ontario', 58, dates),
                region('Alberta', 51, dates),
                region('Atlantic', 45, shortDates),
            ],
        };
    },
};

/**
 * Placeholder KPI source: fabricates deterministic, network-free sample data for
 * any card form, so a form's layout can be built before its real data source
 * exists. It never touches `deps.analyticsStore` and resolves synchronously
 * (`ready`, no loading/error states). Cards on this source are flagged `sample`
 * so the shell renders them grayed out.
 */
export const placeholderSource: KpiSource = {
    sample: true,
    descriptor: {
        label: 'Placeholder',
        metrics: [{ id: 'sample', label: 'Sample' }],
        forms: [
            'number',
            'rate',
            'line',
            'pie',
            'bars',
            'columns',
            'split',
            'downloads',
            'multiline',
            'trio',
            'rate-trio',
        ],
    },
    getCardData(req: KpiCardDataRequest): Promise<KpiCardState> {
        return Promise.resolve({
            status: 'ready',
            // A card-level `sample` (threaded from `KpiCardSpec.sample`) wins over
            // the global per-form default, so one card can carry design-accurate
            // values without touching every other placeholder card of that form.
            data: req.sample ?? SAMPLE_DATA[req.form](req),
        });
    },
};
